import { useState, useEffect, useRef, useCallback } from 'react';
import { useTenant } from '../context/TenantContext.js';
import { tenants as tenantsApi } from '../api/client.js';
import { LoadingSpinner } from '../components/LoadingSpinner.js';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { Badge } from '../components/Badge.js';
import type { SweepMode, BotHealth } from '../types/index.js';

export function SettingsPage() {
  const { activeTenantId, activeTenant, refreshActiveTenant, loading: tenantLoading, error: tenantError, refreshTenants } = useTenant();

  // bot config
  const [botToken, setBotToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [guildId, setGuildId] = useState('');
  const [logChannelId, setLogChannelId] = useState('');
  const [botActive, setBotActive] = useState(false);

  // wallets
  const [walletSolana, setWalletSolana] = useState('');

  // sweep
  const [sweepMode, setSweepMode] = useState<SweepMode>('auto');

  // renewal
  const [renewalEnabled, setRenewalEnabled] = useState(true);
  const [renewalReminderDays, setRenewalReminderDays] = useState(3);
  const [renewalGraceHours, setRenewalGraceHours] = useState(48);

  // saving states
  const [savingToken, setSavingToken] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [saveError, setSaveError] = useState('');

  // bot health check
  const [botHealth, setBotHealth] = useState<BotHealth | null>(null);

  // track initial sync to avoid auto-saving on mount
  const initialSyncDone = useRef(false);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // fetch bot health when tenant changes or bot toggle changes
  useEffect(() => {
    if (!activeTenantId) return;
    let cancelled = false;
    tenantsApi.botHealth(activeTenantId).then((h) => {
      if (!cancelled) setBotHealth(h);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [activeTenantId, botActive]);

  // sync form from tenant
  useEffect(() => {
    if (!activeTenant) return;
    initialSyncDone.current = false;
    setGuildId(activeTenant.guildId);
    setLogChannelId(activeTenant.logChannelId ?? '');
    setBotActive(activeTenant.botActive);
    setWalletSolana(activeTenant.walletSolana ?? '');
    setSweepMode(activeTenant.sweepMode);
    setRenewalEnabled(activeTenant.renewalEnabled);
    setRenewalReminderDays(activeTenant.renewalReminderDays);
    setRenewalGraceHours(activeTenant.renewalGraceHours);
    // mark sync done after state settles
    requestAnimationFrame(() => {
      initialSyncDone.current = true;
    });
  }, [activeTenant]);

  const showSaved = (msg: string) => {
    setSavedMessage(msg);
    setSaveError('');
    setTimeout(() => setSavedMessage(''), 3000);
  };

  const showError = (msg: string) => {
    setSaveError(msg);
    setSavedMessage('');
    setTimeout(() => setSaveError(''), 5000);
  };

  // auto-save function
  const doAutoSave = useCallback(async (overrides?: { botActive?: boolean }) => {
    if (!activeTenantId) return;
    setSavingSettings(true);
    try {
      await tenantsApi.update(activeTenantId, {
        logChannelId,
        walletSolana: walletSolana || undefined,
        sweepMode,
        renewalEnabled,
        renewalReminderDays,
        renewalGraceHours,
        botActive: overrides?.botActive ?? botActive,
      });
      await refreshActiveTenant();
      showSaved('Settings saved automatically');
    } catch (err) {
      console.error('[settings] auto-save failed:', err);
      showError('Failed to save — check your connection');
    } finally {
      setSavingSettings(false);
    }
  }, [activeTenantId, logChannelId, walletSolana, sweepMode, renewalEnabled, renewalReminderDays, renewalGraceHours, botActive, refreshActiveTenant]);

  // debounced auto-save on settings changes
  useEffect(() => {
    if (!initialSyncDone.current) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      void doAutoSave();
    }, 1200);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [logChannelId, walletSolana, sweepMode, renewalEnabled, renewalReminderDays, renewalGraceHours]); // eslint-disable-line react-hooks/exhaustive-deps

  // bot toggle saves immediately (no debounce)
  const handleBotToggle = async () => {
    const newVal = !botActive;
    setBotActive(newVal);
    if (!activeTenantId) return;
    setSavingSettings(true);
    try {
      await tenantsApi.update(activeTenantId, { botActive: newVal });
      await refreshActiveTenant();
      showSaved(newVal ? 'Bot is starting up...' : 'Bot stopped');
    } catch (err) {
      console.error('[settings] bot toggle failed:', err);
      setBotActive(!newVal); // revert
      showError('Failed to toggle bot — try again');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveToken = async () => {
    if (!activeTenantId || !botToken) return;
    setSavingToken(true);
    try {
      await tenantsApi.setBotToken(activeTenantId, botToken);
      setBotToken('');
      await refreshActiveTenant();
      showSaved('Bot token saved securely — toggle the bot on to connect');
    } catch (err) {
      console.error('[settings] save token failed:', err);
      showError('Failed to save bot token');
    } finally {
      setSavingToken(false);
    }
  };

  if (tenantLoading) return <LoadingSpinner text="Loading settings..." />;
  if (tenantError) return <ErrorMessage message={tenantError} onRetry={() => void refreshTenants()} />;
  if (!activeTenant) return <ErrorMessage message="No server selected. You may not be assigned to any server yet." />;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--fd)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Settings</h2>
          <p style={{ fontSize: 13, color: 'var(--t3)' }}>Changes are saved automatically</p>
        </div>
        {savingSettings && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--t3)' }}>
            <div className="spinner" style={{ width: 14, height: 14, borderWidth: 1.5 }} />
            Saving...
          </div>
        )}
      </div>

      {savedMessage && (
        <div className="save-toast">{savedMessage}</div>
      )}
      {saveError && (
        <div className="save-toast" style={{ background: 'var(--red-soft)', borderColor: 'var(--red-border)', color: 'var(--red)' }}>{saveError}</div>
      )}

      <div style={{ display: 'grid', gap: 16 }}>
        {/* Row 1: Bot config + Bot status */}
        <div className="settings-row">
          {/* bot configuration */}
          <div className="kpi settings-card" style={{ padding: 20, flex: 2 }}>
            <div style={{ fontFamily: 'var(--fd)', fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Bot Configuration</div>

            {/* Bot connection status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '10px 14px', background: 'var(--s2)', borderRadius: 10, border: '1px solid var(--b1)' }}>
              {activeTenant.botActive ? (
                <>
                  <div className="server-dot" />
                  <Badge status="active" label="Bot Connected" />
                </>
              ) : activeTenant.hasBotToken ? (
                <>
                  <div className="server-dot inactive" />
                  <Badge status="expired" label="Bot Offline" />
                  <span style={{ fontSize: 12, color: 'var(--t3)', marginLeft: 4 }}>Toggle on below to connect</span>
                </>
              ) : (
                <span style={{ fontSize: 13, color: 'var(--t3)' }}>Paste your bot token below to get started</span>
              )}
            </div>

            {/* Role permission warnings */}
            {botHealth && botHealth.issues.length > 0 && (
              <div style={{ marginBottom: 14, padding: '12px 14px', background: 'var(--red-soft)', border: '1px solid var(--red-border)', borderRadius: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)', marginBottom: 6 }}>
                  Role Permission Issue — Payments will fail
                </div>
                {botHealth.issues.map((issue, i) => (
                  <div key={i} style={{ fontSize: 12, color: 'var(--red)', marginBottom: 4 }}>
                    <strong>{issue.plan}:</strong> {issue.reason}
                  </div>
                ))}
                <div style={{ fontSize: 11, color: 'var(--t2)', marginTop: 8, lineHeight: 1.6 }}>
                  {botHealth.issues.some((i) => i.reason.includes('Manage Roles'))
                    ? <>Go to <strong>Discord Server Settings &rarr; Roles</strong> &rarr; click the bot's role &rarr; enable <strong>"Manage Roles"</strong> permission.</>
                    : <>Go to <strong>Discord Server Settings &rarr; Roles</strong> and drag the bot's role <strong>above</strong> the subscription roles in the list.</>
                  }
                </div>
              </div>
            )}
            {botHealth && !botHealth.online && activeTenant.hasBotToken && (
              <div style={{ marginBottom: 14, padding: '12px 14px', background: 'var(--amber-soft)', border: '1px solid var(--amber-border)', borderRadius: 10 }}>
                <div style={{ fontSize: 13, color: 'var(--amber)' }}>
                  {botHealth.message}
                </div>
              </div>
            )}
            {botHealth && botHealth.online && !botHealth.inGuild && (
              <div style={{ marginBottom: 14, padding: '12px 14px', background: 'var(--amber-soft)', border: '1px solid var(--amber-border)', borderRadius: 10 }}>
                <div style={{ fontSize: 13, color: 'var(--amber)' }}>
                  {botHealth.message}
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Discord Bot Token</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="form-input"
                  type={showToken ? 'text' : 'password'}
                  value={botToken}
                  onChange={(e) => setBotToken(e.target.value)}
                  placeholder={activeTenant.hasBotToken ? 'Token saved — paste to replace' : 'Paste your bot token'}
                  style={{ flex: 1, fontFamily: 'monospace' }}
                />
                <button className="btn-sm btn-ghost" onClick={() => setShowToken(!showToken)}>
                  {showToken ? 'Hide' : 'Show'}
                </button>
                <button
                  className="btn-sm btn-primary"
                  onClick={() => void handleSaveToken()}
                  disabled={savingToken || !botToken}
                >
                  {savingToken ? 'Saving...' : 'Save Token'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 4 }}>
                Encrypted with AES-256-GCM. Never stored in plaintext.
              </div>
            </div>

            <div className="settings-inline-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Guild ID</label>
                <input className="form-input" value={guildId} disabled style={{ fontFamily: 'monospace', opacity: 0.6 }} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Log Channel ID</label>
                <input
                  className="form-input"
                  value={logChannelId}
                  onChange={(e) => setLogChannelId(e.target.value)}
                  placeholder="Channel for payment logs"
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, padding: '12px 14px', background: 'var(--s2)', borderRadius: 10, border: '1px solid var(--b1)' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>Bot Active</div>
                <div style={{ fontSize: 12, color: 'var(--t3)' }}>Start or stop the Discord bot</div>
              </div>
              <div
                className={`toggle ${botActive ? 'active' : ''}`}
                onClick={() => void handleBotToggle()}
              >
                <div className="toggle-dot" />
              </div>
            </div>
          </div>

          {/* security card */}
          <div className="kpi settings-card" style={{ padding: 20, flex: 1 }}>
            <div style={{ fontFamily: 'var(--fd)', fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Security</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>Two-Factor Authentication</div>
                <div style={{ fontSize: 12, color: 'var(--t3)' }}>Protect your dashboard with TOTP 2FA</div>
              </div>
              <Badge status="active" label="Enabled" />
            </div>
          </div>
        </div>

        {/* Row 2: Wallets + Payment settings side by side */}
        <div className="settings-row">
          {/* destination wallets */}
          <div className="kpi settings-card" style={{ padding: 20, flex: 1 }}>
            <div style={{ fontFamily: 'var(--fd)', fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Destination Wallets</div>

            <div className="form-group">
              <label className="form-label">Solana Wallet</label>
              <input
                className="form-input"
                value={walletSolana}
                onChange={(e) => setWalletSolana(e.target.value)}
                placeholder="Enter your SOL wallet address"
                style={{ fontFamily: 'monospace' }}
              />
            </div>

          </div>

          {/* sweep + renewal */}
          <div className="kpi settings-card" style={{ padding: 20, flex: 1 }}>
            <div style={{ fontFamily: 'var(--fd)', fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Payment Settings</div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>Sweep Mode</div>
                <div style={{ fontSize: 12, color: 'var(--t3)' }}>How payments are moved to your wallet</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['auto', 'manual'] as SweepMode[]).map((mode) => (
                  <div
                    key={mode}
                    className={`filter-pill ${sweepMode === mode ? 'active' : ''}`}
                    onClick={() => setSweepMode(mode)}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {mode}
                  </div>
                ))}
              </div>
            </div>

            {/* renewal settings inline */}
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--b1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>Auto-Renewal Reminders</div>
                  <div style={{ fontSize: 12, color: 'var(--t3)' }}>Send renewal DMs before expiry</div>
                </div>
                <div
                  className={`toggle ${renewalEnabled ? 'active' : ''}`}
                  onClick={() => setRenewalEnabled(!renewalEnabled)}
                >
                  <div className="toggle-dot" />
                </div>
              </div>

              {renewalEnabled && (
                <div style={{ marginTop: 12 }}>
                  <div className="settings-inline-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="form-label">Reminder Days</label>
                      <input
                        className="form-input"
                        type="number"
                        min="1"
                        max="30"
                        value={renewalReminderDays}
                        onChange={(e) => setRenewalReminderDays(Number(e.target.value))}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="form-label">Grace Period (hours)</label>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        max="168"
                        value={renewalGraceHours}
                        onChange={(e) => setRenewalGraceHours(Number(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
